import { useEffect } from "react";

/**
 * Hook that alerts clicks outside of the passed ref
 * @param {React.RefObject} ref - Ref of the component to detect click outside of
 * @param {function} handler - Function to be called when a click is detected outside of the component
 * @returns {void}
 * @description This hook is used to detect click outside of a component. It takes a ref and a handler function as arguments. When a click is detected outside of the component, the handler function is called with the event as an argument.
 * @example
 * const ref = useRef(null);
 * useOnClickOutside(ref, () => //console.log("Clicked outside"));
 * <div ref={ref}>Click outside to trigger the handler</div>
 */
const useOnClickOutside = (ref, handler) => {
  const node = ref.current;

  useEffect(() => {
    const listener = (event) => {
      if (!node || node.contains(event.target)) return;
      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [handler, node]);
};

export default useOnClickOutside;
